import { api } from "./http"
import { WEB_CATALOG_GROUP } from "./config"
import type {
	AddReport,
	CreatedCatalog,
	CreatedSite,
	NewSiteFields,
	ProductGroup,
	RemoveReport,
	TranslateSource,
	TranslatedByMachine,
} from "./types"
import { PartialRefreshTable, ProductNameAttributeStrategy, WebCategoryType } from "./types"

/**
 * Запись в ecm. Все методы — ровно те же, что дёргает старый фронт;
 * права проверяет бэк (RoleAuthorize + *AuthHelper), UI их только повторяет.
 *
 * Здесь только вызовы. Занятость, ошибка и сброс кэша — общий `useAction`
 * (`hooks/useAction.ts`) и его надстройка для карточки группы
 * (`screens/group/useWriter.ts`).
 */

export const groupWrites = {
	create: (body: {
		id: string
		autoId: string
		name: string
		isToolset: boolean
		languageId: number
	}) =>
		api.post<void>("/api/product-group/create", {
			seriesId: null,
			signId: null,
			sellmarkId: null,
			mainAttributeId: null,
			productIds: [],
			...body,
		}),

	/** Тело — вся карточка целиком: бэк сверяет присланное с базой и
	 *  запрещает менять то, что роли не положено (ProductGroupControllerAuthHelper.cs:24-65). */
	update: (group: ProductGroup, languageId: number) =>
		api.put<void>("/api/product-group/update", {
			id: group.id,
			name: group.name,
			description: group.description,
			descriptionWeb: group.descriptionWeb,
			seriesId: group.seriesId,
			signId: group.signId,
			sellmarkId: group.sellmarkId,
			priceGroupId: group.priceGroupId ?? 0,
			mainAttributeId: group.mainAttributeId,
			isToolset: group.isToolset,
			isImageChecked: group.isImageChecked,
			isDescriptionChecked: group.isDescriptionChecked,
			siteId: group.siteId ?? 0,
			languageId,
		}),
}

export const productWrites = {
	add: (productGroupId: string, productIds: string[]) =>
		api.post<void>("/api/product/add-product-to-group", {
			productGroupId,
			productIds,
		}),

	remove: (productGroupId: string, productId: string) =>
		api.delete<void>("/api/product/remove-product-from-group", {
			productGroupId,
			productId,
		}),

	replace: (productGroupId: string, productId: string, newProductId: string) =>
		api.put<void>("/api/product/replace-products-in-group", {
			productGroupId,
			productId,
			newProductId,
		}),

	/** Перенос на позицию 1..N — бэк сам перенумеровывает остальных. */
	move: (
		productGroupId: string,
		productId: string,
		currentSort: number,
		targetSort: number
	) =>
		api.put<void>("/api/product/move-product-sort", {
			productGroupId,
			productId,
			currentSort,
			targetSort,
		}),

	saveValues: (
		productGroupId: string,
		values: { productId: string; attributeId: number; value: string }[]
	) =>
		api.put<void>("/api/product/change-attribute-value-in-group", {
			productGroupId,
			values,
		}),

	/** Глобальная пересборка `Products.GeneratedNameJson` — то же самое, что
	 *  кнопка «Сгенерировать короткие имена продуктов» на форме переводчика
	 *  в ECM (`TranslateForm.tsx`). Проходит по всей базе разом
	 *  (`ProductGeneratedNameManager.cs:26-84`), группового варианта у бэка
	 *  нет. Без роли Admin ECM всегда шлёт `ByAttributeShortNameAndAttributeName` —
	 *  делаем то же самое, выбора стратегии в интерфейсе Риги нет. */
	generateShortNames: () =>
		api.put<void>("/api/product/generate-short-names", {
			attributeNameStrategy:
				ProductNameAttributeStrategy.ByAttributeShortNameAndAttributeName,
		}),
}

export const attributeWrites = {
	add: (productGroupId: string, attributeId: number) =>
		api.post<void>("/api/attributes/add-attribute", {
			productGroupId,
			attributeId,
		}),

	remove: (productGroupId: string, attributeId: number) =>
		api.delete<void>("/api/attributes/remove-attribute", {
			productGroupId,
			attributeId,
		}),

	/** Порядок колонок бэк умеет только менять местами с соседом. */
	swap: (
		productGroupId: string,
		attributeId: number,
		targetAttributeId: number
	) =>
		api.put<void>("/api/attributes/change-attribute-ordering", {
			productGroupId,
			attributeId,
			targetAttributeId,
		}),

	toggleImportant: (groupId: string, attributeId: number) =>
		api.put<void>("/api/attributes/important-status", { groupId, attributeId }),

	/** Приоритет среди важных. Первое место закреплено за главным атрибутом,
	 *  поэтому и currentSort, и targetSort всегда ≥ 2 (SwapImportantSortRequest). */
	swapImportant: (
		productGroupId: string,
		attributeId: number,
		targetAttributeId: number,
		currentSort: number,
		targetSort: number
	) =>
		api.put<void>("/api/attributes/important-sort", {
			productGroupId,
			attributeId,
			targetAttributeId,
			currentSort,
			targetSort,
		}),

	/** Короткое название атрибута и единица измерения на выбранном языке.
	 *  Хранятся не в `Translates`, а в json-полях атрибута, поэтому и правятся
	 *  отдельными методами. */
	shortName: (attributeId: number, shortname: string, languageId: number) =>
		api.put<void>("/api/attributes/change-attribute-shortname", {
			attributeId,
			shortname,
			languageId,
		}),

	unit: (attributeId: number, unit: string, languageId: number) =>
		api.put<void>("/api/attributes/change-attribute-unit", {
			attributeId,
			unit,
			languageId,
		}),
}

/**
 * Состав набора (комплектация). Бэк проверяет принадлежность `toolsetId`
 * (товара-набора) переданной группе на каждом методе и делит права по
 * принадлежности самой группы (`ToolsetControllerAuthHelper`): в латвийских
 * наборах пишут Lv и Rg, в российских — только RF, поэтому там кнопки записи
 * скрыты (`rights.toolsetControl`).
 */
export const toolsetWrites = {
	/** Пиктограмма по умолчанию необязательна: `null`, если раздел не выбран. */
	add: (
		productGroupId: string,
		toolsetId: string,
		products: { productId: string; pictogramId: number | null; sort: number }[]
	) =>
		api.post<void>("/api/toolsets/add-to-toolset", {
			productGroupId,
			toolsetId,
			products,
		}),

	remove: (productGroupId: string, toolsetId: string, productId: string) =>
		api.delete<void>("/api/toolsets/remove-from-toolset", {
			productGroupId,
			toolsetId,
			productId,
		}),

	replace: (
		productGroupId: string,
		toolsetId: string,
		productId: string,
		anotherProductId: string
	) =>
		api.put<void>("/api/toolsets/change-assignment", {
			productGroupId,
			toolsetId,
			productId,
			anotherProductId,
		}),

	/** Перенос на позицию 1..N — бэк сам перенумеровывает остальных, как и
	 *  у товаров карточки (`move-product-sort`). */
	moveSort: (
		productGroupId: string,
		toolsetId: string,
		productId: string,
		currentSort: number,
		targetSort: number
	) =>
		api.put<void>("/api/toolsets/move-sort", {
			productGroupId,
			toolsetId,
			productId,
			currentSort,
			targetSort,
		}),

	changePictogram: (
		productGroupId: string,
		toolsetId: string,
		productId: string,
		pictogramId: number
	) =>
		api.put<void>("/api/toolsets/change-pict", {
			productGroupId,
			toolsetId,
			productId,
			pictogramId,
		}),
}

/**
 * Категории группы на сайте. Все четыре метода работают только с листьями дерева:
 * категорию с детьми бэк не примет («Category has children»,
 * `CategoryService.cs:382-383`).
 *
 * `catalogId` для веб-категорий не нужен — он обязателен только у печатного
 * каталога. Строк в базе создаётся столько, сколько заведено веб-каталогов,
 * но интерфейсу они видны как одна категория (`.Distinct()` на чтении).
 */
export const categoryWrites = {
	/** Первая категория группы автоматически становится главной
	 *  (`CategoryService.cs:391-405`). */
	add: (productGroupId: string, categoryId: number) =>
		api.post<void>("/api/category/add-product-groups-to-cats", {
			productGroupIds: [productGroupId],
			categoriesIds: [categoryId],
			catalogGroup: WEB_CATALOG_GROUP,
			catalogId: null,
		}),

	/** Список групп бэк разбирает из строки через запятую. Если убрали главную,
	 *  он сам назначает главной следующую и возвращает её id. */
	remove: (productGroupId: string, categoryId: number) =>
		api.delete<number>("/api/category/remove-product-groups-from-cats", {
			productGroupIds: productGroupId,
			catalogGroup: WEB_CATALOG_GROUP,
			categoryId,
		}),

	/** Замена категории на месте: признак «главная» сохраняется за строкой. */
	change: (productGroupId: string, categoryId: number, newCategoryId: number) =>
		api.put<void>("/api/category/change-product-cat-group", {
			productGroupId,
			categoryId,
			newCategoryId,
			catalogGroup: WEB_CATALOG_GROUP,
			catalogId: null,
		}),

	setMain: (productGroupId: string, categoryId: number) =>
		api.put<void>("/api/category/set-cat-as-main", {
			productGroupId,
			categoryId,
			catalogGroup: WEB_CATALOG_GROUP,
		}),
}

/**
 * Справочник переводов.
 *
 * Запись идёт одним методом на всю пачку правок — `save-translated-values`.
 * Старый фронт на этом экране сохранял каждую строку отдельным `PUT /api/translate`,
 * хотя это тот же `TranslateEditor.UpdateOrCreateAsync`, только с массивом из
 * одного элемента.
 *
 * **Важное поведение бэка.** Перевод сохраняется не в одну строку, а во все
 * фразы того же раздела с точно таким же русским текстом
 * (`TranslateFinder.FindTranslatesWithSameTranslateAsync`), причём по всей базе,
 * а не только по текущему каталогу. Пустой русский текст не разносится.
 * Отсюда два следствия для интерфейса: показывать, сколько строк набора уедут
 * вместе с текущей, и не пускать в одну пачку разные переводы одной русской
 * фразы — бэк молча возьмёт первый попавшийся (`TranslateEditor.cs:137-140`).
 */
export const translateWrites = {
	saveBatch: (
		targetLanguageId: number,
		translator: string,
		translates: {
			translateId: number
			sourceId: string
			sourceTable: TranslateSource
			translate: string
		}[]
	) =>
		api.put<void>("/api/translate/save-translated-values", {
			targetLanguageId,
			translator,
			translates,
		}),

	/** Построчное сохранение. Нужно только роли `Translator`: пакетный метод
	 *  её в `RoleAuthorize` не перечисляет, а этот — да
	 *  (`TranslateController.cs:125,143`). Внутри тот же `UpdateOrCreateAsync`. */
	updateOne: (
		languageId: number,
		row: { translateId: number; sourceId: string; source: TranslateSource },
		translate: string
	) =>
		api.put<string>("/api/translate", {
			translateId: row.translateId,
			sourceId: row.sourceId,
			translateSource: row.source,
			translate,
			languageId,
		}),

	/** Машинный перевод с русского. Ответ — пары «оригинал → перевод», порядок
	 *  не обещан. Идёт в DeepL прямо с рижского узла (`/ai/translate`), не через
	 *  российский ecm: тот ходил через туннель на российском сервере, и, когда
	 *  туннель лёг, перевод в Риге встал целиком. Язык — код DeepL из
	 *  `/api/translate/lang`, не id. */
	byMachine: (targetLanguageCode: string, values: string[]) =>
		api.post<TranslatedByMachine[]>("/ai/translate", {
			sourceLanguageCode: "RU",
			targetLanguageCode,
			values,
		}),

	/** «Собрать новые фразы» (`gen-new`): добавляет русские строки для объектов,
	 *  которых ещё нет в `Translates`. Только добавляет — ничего не удаляет и не
	 *  меняет (`TranslateService.cs:40-67`). Без этого новые группы, атрибуты и
	 *  категории в справочнике не появятся.
	 *
	 *  Что считается «новым», решает `dbo.GetNewTranslates()`: она сравнивает
	 *  тройку «текст + код объекта + раздел» с тем, что уже лежит в `Translates`.
	 *  Отсюда два следствия. Переименовали объект — появится ещё одна строка,
	 *  а старая останется навсегда. Описания групп попадают в справочник, только
	 *  когда у группы проставлена галка «описание проверено» на русском. */
	generateNew: () => api.put<void>("/api/translate/gen-new", null),

	/**
	 * «Применить переводы» (`update-sources-from-translates`) — то, ради чего
	 * справочник вообще существует: перевод из `Translates` раскладывается по
	 * рабочим таблицам, откуда его берут выгрузка на сайт и печатный каталог.
	 *
	 * Что делает бэк (`TranslateService.cs:69-199`):
	 *
	 * - `AttrNames`, `CatWeb`, `CatKat`, `PictGroup` — пишет перевод в `NameJson`
	 *   соответствующей таблицы по коду объекта. Просто и безопасно;
	 * - `PictGroupDesc` — собирает заново **описание и название** группы:
	 *   русское описание режется по «•», каждый пункт ищется в справочнике по
	 *   точному совпадению текста, найденные заменяются переводом, **ненайденные
	 *   выбрасываются**, результат пишется в `DescriptionJSON` и
	 *   `DescriptionWebJSON` целевого языка. Если переводов нет вовсе, описание
	 *   на этом языке станет пустым. Отсюда предупреждения в интерфейсе.
	 *
	 * Раздел `GroupName` в списке не нужен: названия групп обновляются внутри
	 * ветки `PictGroupDesc`, а при обычном сохранении перевода — сразу, ещё и
	 * без всякой кнопки (`TranslateToSourceProxy`, он же пишет названия и
	 * описания товаров). `ValueDescription` не применяется никуда и не должен:
	 * его подставляют на лету при выгрузке на сайт и в Excel каталога
	 * (`SiteCatalogService.TranslateValueDescriptions`).
	 *
	 * Операция глобальная: каталог она не смотрит, обновляет всю базу на
	 * выбранном языке.
	 */
	applyToSources: (languageId: number, types: TranslateSource[]) =>
		api.put<void>("/api/translate/update-sources-from-translates", {
			languageId,
			types,
		}),
}

/**
 * Пиктограммы группы.
 *
 * Права бэк делит по принадлежности группы (`PictogramControllerAuthHelper`):
 * латвийские группы ведут `ContentManagerLv` и `ContentManagerRg`, российские
 * и ничейные — только `ContentManagerRF` или фотограф. То есть в РФ-группах
 * Рига пиктограммы видит, но не правит; интерфейс это повторяет
 * (`rights.pictograms`).
 */
export const pictogramWrites = {
	add: (productGroupId: string, pictogramId: number) =>
		api.post<void>("/api/pictograms", { productGroupId, pictogramId }),

	remove: (productGroupId: string, pictogramId: number) =>
		api.delete<void>("/api/pictograms", { productGroupId, pictogramId }),

	/** Замена одной пиктограммы другой — одним запросом, без «убрать и добавить». */
	change: (productGroupId: string, pictogramId: number, newPictogramId: number) =>
		api.put<void>("/api/pictograms", {
			productGroupId,
			pictogramId,
			newPictogramId,
		}),
}

/**
 * Фото группы. Сам файл бэк не принимает: в `ImagesToProductGroups` пишется
 * только имя, а файл латвийской группы ищется в
 * `DesignMaterials/Latvia_ALL_Photos` на российской шаре — туда его привозит
 * GoodSync из папки FotoLV рижан. Старый фронт делает то же самое: шлёт
 * `file.name` (`ImageBlock.tsx`). Удаление убирает строку в базе, файл на
 * шаре остаётся.
 */
export const imageWrites = {
	add: (productGroupId: string, imageType: number, imageName: string) =>
		api.post<void>("/api/images", { productGroupId, imageType, imageName }),

	remove: (productGroupId: string, imageType: number) =>
		api.delete<void>("/api/images", { productGroupId, imageType }),
}

/**
 * Публикация на сайт — та часть, что живёт в **базе ERP**, а не на сайте.
 *
 * Здесь остались только методы `ecm`, которые в MySQL сайта не ходят: состав
 * каталога сайта. Всё, что трогает сам сайт, переехало в `siteWrites` —
 * в базы сайтов Риги ходит только рижский узел.
 *
 * `partialRefresh` пока лежит здесь без кнопки: метод в `ecm` открывает
 * соединение с MySQL сайта из России и на неудаче роняет процесс целиком, а
 * рижского двойника у него ещё нет. Кнопка погашена (`VIA_RU_NOTE`), вызов
 * оставлен, чтобы контракт не пришлось восстанавливать заново, когда двойник
 * появится. `fillTable` (заливка таблиц OpenCart целиком) убран вместе с
 * массовой загрузкой на `/publish` — тем же ограничением она и была
 * заблокирована, а версии для Риги так и не появилось.
 *
 * Чего здесь намеренно нет. `remove-catalog-to-site` и
 * `groups/status/show|unshow` — это система «выводить / не выводить», от
 * которой Ригу решено избавить: загрузили — значит на сайте, убрали — значит
 * нет. Галку, которую бэк всё ещё требует, интерфейс проставляет сам
 * (`components/site/GroupSitePanel.tsx`). Методы в ecm остаются, пока систему
 * не уберут и в нём.
 */
export const websiteWrites = {
	/** Переносит группы каталога-источника в каталог сайта вместе с их
	 *  веб-категориями. Существующие строки не трогает, только доставляет
	 *  недостающие и выравнивает статус вывода (`SiteCatalogService.cs:80-119`).
	 *  Живёт в MSSQL — сайта не касается. */
	addCatalog: (catalogId: number, websiteId: number) =>
		api.put<void>("/api/websites/add-catalog-to-site", {
			catalogId,
			websiteId,
		}),

	/** Точечное обновление уже загруженного сайта: вес, наименования,
	 *  список категорий, картинки разделов. Ничего не чистит.
	 *  Тоже через Россию — кнопки нет, см. `VIA_RU_NOTE`. */
	partialRefresh: (
		websiteId: number,
		catalogId: number,
		table: PartialRefreshTable,
		type: WebCategoryType = WebCategoryType.Rg
	) =>
		api.put<void>("/api/websites/partial-refresh", {
			websiteId,
			catalogId,
			table,
			type,
		}),
}

/**
 * Группа на сайте — **рижский BFF**, а не `ecm`.
 *
 * Правило проекта: в базы сайтов Риги ходит только рижский узел. Он читает
 * строки выгрузки теми же функциями ERP (`dbo.GetOc*`), что и `ecm`, но
 * подключается к MySQL сайта сам — из Риги эта база отвечает, из России нет.
 *
 * Отличий от `ecm` два, и оба в нашу пользу: каждая операция ложится в журнал
 * на диск до записи (откат возможен даже после падения процесса, чего у `ecm`
 * на MyISAM нет), а недоступная база отвечает текстом, а не убивает процесс.
 *
 * Галку «выводить» рижский узел не трогает: она в MSSQL и остаётся за `ecm`
 * (`catalogWrites.setShowStatus`). Без неё ERP просто не отдаст строк, и BFF
 * ответит блокировкой «У группы снят вывод».
 */
export const siteWrites = {
	add: (groupId: string, websiteId: number) =>
		api.post<AddReport>("/ai/site/group/add", { groupId, websiteId }),

	remove: (groupId: string, websiteId: number) =>
		api.post<RemoveReport>("/ai/site/group/remove", { groupId, websiteId }),
}

/** Галка «Выводить в кат.» — строка привязки группы к каталогу в базе ERP.
 *  Бэк принимает пачку только по одной группе за раз
 *  (`CatalogController.cs:74-78`). */
export const catalogWrites = {
	setShowStatus: (
		rows: {
			productGroupId: string
			catalogId: number
			webCategoryId: number | null
			catalogCategoryId: number | null
			showStatus: boolean
		}[]
	) => api.put<void>("/api/catalogs/change-show-status", rows),
}

/**
 * Назначение товаров в каталоги и заведение новых сайтов/каталогов —
 * **рижский BFF** (`/ai/catalog/*`), не `ecm`: у Риги нет способа прочитать
 * состав каталога через ecm (роль не пускает в `by-catalog`), а создания
 * сайтов и печатных каталогов в API ecm нет вовсе.
 *
 * Пишет прямо в ERP (MSSQL), базы сайтов не касается — поэтому, в отличие от
 * `siteWrites`, здесь нет журнала и отката: записи идемпотентны и ничего не
 * удаляют (та же гарантия, что у `websiteWrites.addCatalog`).
 */
export const catalogAdminWrites = {
	/** Назначает список групп в каталог — по одной группе через
	 *  `SiteDirectory.AttachToCatalogAsync` на бэке, копией веб-категорий.
	 *  Существующие строки не трогает: 0 в ответе по группе значит «уже была
	 *  в каталоге». */
	attachGroups: (catalogId: number, groupIds: string[]) =>
		api.post<Record<string, number>>("/ai/catalog/attach", {
			catalogId,
			groupIds,
		}),

	/** Печатный каталог — только имя, `Admin`-only на бэке. */
	createPrinted: (name: string) =>
		api.post<CreatedCatalog>("/ai/catalog/create-printed", { name }),

	/** Новый сайт Риги: `Schema` бэк ставит сам (`Latvian`), парный каталог
	 *  того же имени заводит в той же транзакции. `Admin`-only на бэке — форма
	 *  кладёт в ERP боевые реквизиты FTP и БД сайта. */
	createSite: (fields: NewSiteFields) =>
		api.post<CreatedSite>("/ai/catalog/create-site", fields),
}
